import { HubConnectionBuilder } from '@microsoft/signalr';
import { SIGNALR_X_FUNCTIONS_KEY } from '../core/env/config';
import { ISignalRService } from './signalR.types';
import { getEnvironment } from '../core/env/environment';

export class SignalRService implements ISignalRService {
  private static instance: SignalRService;
  private connectionID: string | null = null;
  private updatesQueue: Map<string, Record<string, string[]>[]> = new Map();
  connection: ISignalRService['connection'];
  update: ISignalRService['update'];

  private constructor(accessTokenFactory: () => Promise<string | null>) {
    const sendManageRequest = this.makeSendManageRequest(accessTokenFactory);

    const connection = this.createNewSignalRConnection({ accessTokenFactory });
    connection.start().then(() => {
      console.log(`SignalR connection established with ID: ${connection.connectionId}`);
      this.connectionID = connection.connectionId;
      Array.from(this.updatesQueue.entries()).forEach(([attributeName, queue]) => {
        if (queue.length) {
          sendManageRequest({ [attributeName]: queue[0] });
        }
        queue.length = 0;
      });
    });
    this.connection = connection;

    this.update = (attributeName, body) => {
      if (this.connectionID === null) {
        const queue = this.updatesQueue.get(attributeName);
        if (!queue) {
          this.updatesQueue.set(attributeName, [body]);
        } else {
          queue.unshift(body);
        }
      } else {
        sendManageRequest({ [attributeName]: body });
      }
    };
  }

  public static getService(accessTokenFactory: () => Promise<string | null>) {
    if (!this.instance) {
      this.instance = new SignalRService(accessTokenFactory);
    }
    return this.instance;
  }

  private makeSendManageRequest(accessTokenFactory: () => Promise<string | null>) {
    const environment = getEnvironment();
    return async (body: Record<string, Record<string, string[]>>) => {
      const accessToken = await accessTokenFactory();
      if (!accessToken) {
        return;
      }
      fetch(environment.signalREndpoint + 'client-manage-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-functions-key': SIGNALR_X_FUNCTIONS_KEY,
          Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          ...body,
          connectionID: this.connectionID
        })
      }).catch(() => {});
    };
  }

  private createNewSignalRConnection({ accessTokenFactory }: { accessTokenFactory: () => Promise<string | null> }) {
    function connect() {
      const environment = getEnvironment();
      const connection = new HubConnectionBuilder()
        .withUrl(environment.signalREndpoint, {
          accessTokenFactory: async () => {
            const token = accessTokenFactory ? await accessTokenFactory() : null;
            return token || '';
          }
        })
        .withAutomaticReconnect()
        .build();
      connection.onreconnecting(() => {
        console.log('SignalR connection is attempting to reconnect');
      });
      connection.onreconnected(() => {
        console.log('SignalR connection reconnected');
      });
      connection.onclose(() => {
        console.log('SignalR connection disconnected, manually reconnecting');
        // Manually reconnect after 3 seconds as withAutomaticReconnect already attempts to reconnect using exponential backoff
        setTimeout(() => connect(), 2000);
      });
      return connection;
    }

    return connect();
  }
}
